const UPSTASH_URL = import.meta.env.VITE_UPSTASH_REDIS_REST_URL as string;
const UPSTASH_TOKEN = import.meta.env.VITE_UPSTASH_REDIS_REST_TOKEN as string;

export interface AnalyticsEntry {
  url: string;
  category: string;
  latency: number;
  count: number;
  verified: boolean | string;
}

interface RedisResponse {
  result: unknown;
}

const headers = () => ({
  Authorization: `Bearer ${UPSTASH_TOKEN}`,
});

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export async function fetchAllAnalytics(): Promise<AnalyticsEntry[]> {
  // 1. SCAN for all classify:* keys
  const allKeys: string[] = [];
  let cursor = '0';

  do {
    const scanUrl = `${UPSTASH_URL}/scan/${cursor}/match/classify:*/count/100`;
    const res = await fetch(scanUrl, { headers: headers() });
    if (!res.ok) throw new Error(`Redis SCAN failed: ${res.status}`);
    const data: RedisResponse = await res.json();
    const [nextCursor, keys] = data.result as [string, string[]];
    cursor = nextCursor;
    allKeys.push(...keys);
  } while (cursor !== '0');

  if (allKeys.length === 0) return [];

  // 2. Batch MGET in chunks of 80 to avoid URL-too-long
  const chunks = chunkArray(allKeys, 80);
  const allValues: (string | null)[] = [];

  for (const chunk of chunks) {
    const mgetUrl = `${UPSTASH_URL}/mget/${chunk.map(k => encodeURIComponent(k)).join('/')}`;
    const res = await fetch(mgetUrl, { headers: headers() });
    if (!res.ok) throw new Error(`Redis MGET failed: ${res.status}`);
    const data: RedisResponse = await res.json();
    allValues.push(...(data.result as (string | null)[]));
  }

  // 3. Parse results
  const entries: AnalyticsEntry[] = [];

  for (let i = 0; i < allKeys.length; i++) {
    const value = allValues[i];
    if (!value) continue;

    try {
      const url = allKeys[i].replace(/^classify:/, '');
      const parsed = JSON.parse(value);

      let verifiedValue: boolean | string = false;
      if (parsed.verified === 'true' || parsed.verified === true) {
        verifiedValue = true;
      } else if (parsed.verified && parsed.verified !== 'false') {
        verifiedValue = parsed.verified;
      }

      entries.push({
        url,
        category: parsed.category || 'Uncategorized',
        latency: parsed.latency || 0,
        count: parsed.count || 1,
        verified: verifiedValue,
      });
    } catch {
      // skip unparseable entries
    }
  }

  entries.sort((a, b) => b.count - a.count);
  return entries;
}

export async function updateUrlEntry({
  url,
  verified,
  category,
}: {
  url: string;
  verified?: boolean;
  category?: string;
}): Promise<void> {
  const redisKey = `classify:${url}`;

  // GET existing value
  const getUrl = `${UPSTASH_URL}/get/${encodeURIComponent(redisKey)}`;
  const getRes = await fetch(getUrl, { headers: headers() });
  if (!getRes.ok) throw new Error(`Redis GET failed: ${getRes.status}`);

  const getData: RedisResponse = await getRes.json();
  const existingValue = getData.result as string | null;
  if (!existingValue) throw new Error('URL not found in cache');

  const parsedData = JSON.parse(existingValue);

  // Handle category change
  if (category !== undefined && category !== parsedData.category) {
    parsedData.verified = parsedData.category;
    parsedData.category = category;
  } else if (verified !== undefined) {
    parsedData.verified = verified ? 'true' : 'false';
  }

  // SET updated value
  const setUrl = `${UPSTASH_URL}/set/${encodeURIComponent(redisKey)}/${encodeURIComponent(JSON.stringify(parsedData))}`;
  const setRes = await fetch(setUrl, { headers: headers() });
  if (!setRes.ok) throw new Error(`Redis SET failed: ${setRes.status}`);
}
